import React, {Suspense} from 'react';
import {useTranslation} from "@twigsby/frontend-framework";
import {AuthProvider} from "react-oauth2-code-pkce";
import {
    createBrowserRouter, isRouteErrorResponse,
    Outlet,
    RouterProvider,
    ScrollRestoration,
    useRouteError
} from "react-router-dom";
import Navigation from "./Components/Navigation";
import ProtectedRoute from "./Components/ProtectedRoute";
import {SiteDevMenu} from "./Components/SiteDevMenu";
import {SiteGlobalComponents} from "./Components/SiteGlobalComponents";
import ErrorPage from "./Pages/ErrorPage";
import MessageCentreProvider from "./Providers/MessageCentreProvider";
import NewMailboxProvider from "./Providers/NewMailboxProvider";
import SiteInterfaceProvider from "./Providers/SiteInterfaceProvider";
import UKPBAuthProvider, {authConfig} from "./Providers/AuthProvider";
import {DevMenu, LayoutProvider, Layouts} from '@twigsby/frontend-framework';
import {IPMReactQueryProvider} from '@twigsby/client-data';
import UserProvider from "./Providers/UserProvider";
import MailRoutes from "./Routes/MailRoutes";
import MessageCentreRoutes from "./Routes/MessageCentreRoutes";
import {PublicRoutes} from "./Routes/PublicRoutes";
import SignUpRoutes from "./Routes/SignUpRoutes";
import TopRoutes from "./Routes/TopRoutes";
import { Header } from './Components/Header';

import './i18n';
import './Styles/custom-styles.scss';

const RouteErrorElement = React.lazy(() => import("./Pages/RouteErrorPage"));
const AuthorisationErrorElement = React.lazy(() => import("./Pages/AuthorisationErrorPage"));

const Layout = (props: any) => {
    return (
        <AuthProvider authConfig={authConfig}>
            <UKPBAuthProvider>
                <IPMReactQueryProvider showDevTools={true}>
                    <UserProvider>
                        <SiteInterfaceProvider storageKey={process.env.REACT_APP_SITE_KEY}>
                            <LayoutProvider
                                defaultLayout={Layouts.SIDEBAR_LAYOUT}
                                headerComponent={<Header />}
                                sidebarComponent={<Navigation showLogo={true} showMenuModifier={true} isBranded={true} />}
                                devComponent={ <DevMenu customEntries={<SiteDevMenu />} /> }
                                projectStorageKey={process.env.REACT_APP_SITE_KEY}
                            >
                                <SiteGlobalComponents />

                                {props.children}

                                <Outlet />

                                <ScrollRestoration />
                            </LayoutProvider>
                        </SiteInterfaceProvider>
                    </UserProvider>
                </IPMReactQueryProvider>
            </UKPBAuthProvider>
        </AuthProvider>
    )
};

const ErrorMapper = () => {
    const error: any = useRouteError();
    const { t } = useTranslation();

    if (!isRouteErrorResponse(error)) {
        return (
            <Layout>
                <ErrorPage error={error || new Error('An Error Occurred')} />
            </Layout>
        )
    }

    return (
        <Layout>
            <Suspense>
                {error.status === 404 && (
                    <RouteErrorElement {...error} description={t('routeErrorPage.messages.404')} />
                )}

                {error.status === 401 && (
                    <AuthorisationErrorElement />
                )}

                {error.status === 503 && (
                    <RouteErrorElement {...error} description={t('routeErrorPage.messages.503')} />
                )}

                {error.status === 418 && (
                    <RouteErrorElement {...error} description={t('routeErrorPage.messages.418')} />
                )}
            </Suspense>
        </Layout>
    )
}

const appRouter = createBrowserRouter([
    {
        element: <Layout />,
        errorElement: <ErrorMapper />,
        children: [
            ...PublicRoutes,
            ...SignUpRoutes,
            {
                element: <ProtectedRoute />,
                children: [
                    ...TopRoutes,
                    {
                        element:
                            <NewMailboxProvider>
                                <ProtectedRoute />
                            </NewMailboxProvider>,
                        children: [
                            ...MailRoutes
                        ]
                    },
                    {
                        element:
                            <MessageCentreProvider>
                                <ProtectedRoute />
                            </MessageCentreProvider>,
                        children: [
                            ...MessageCentreRoutes
                        ]
                    }
                ]
            }
        ]
    },
], {
    future: {
        v7_normalizeFormMethod: true,
    },
});

const App = () => <RouterProvider router={appRouter} />

export default App;
